import { useQuery } from '@tanstack/react-query'
import { ExternalLink, Search } from 'lucide-react'
import { useState, type FormEvent } from 'react'

import { searchTrademarks, type TrademarkRecord } from '../lib/api'

export function TrademarksPage() {
  const [draftQuery, setDraftQuery] = useState('')
  const [searchQuery, setSearchQuery] = useState('')

  const trademarksQuery = useQuery({
    queryKey: ['trademarks', searchQuery],
    queryFn: () => searchTrademarks({ query: searchQuery, limit: 25 })
  })

  const records = trademarksQuery.data?.results ?? []

  const submitSearch = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    setSearchQuery(draftQuery.trim())
  }

  return (
    <section className="trademarksPage">
      <div className="toolbar">
        <div>
          <p className="eyebrow">Source records</p>
          <h1>Trademarks</h1>
        </div>
        <form className="searchBox" role="search" onSubmit={submitSearch}>
          <label htmlFor="trademark-search">Search</label>
          <div>
            <input
              id="trademark-search"
              value={draftQuery}
              onChange={(event) => {
                setDraftQuery(event.target.value)
              }}
              placeholder="Serial, mark, or owner"
            />
            <button className="primaryButton" type="submit" disabled={trademarksQuery.isFetching}>
              <Search size={18} aria-hidden="true" />
              <span>{trademarksQuery.isFetching ? 'Searching' : 'Search'}</span>
            </button>
          </div>
        </form>
      </div>

      <div className="trademarkStats">
        <Metric label="Query" value={trademarksQuery.data?.query || 'Latest'} />
        <Metric label="Results" value={String(records.length)} />
        <Metric
          label="Status"
          value={trademarksQuery.isFetching ? 'Refreshing' : trademarksQuery.error?.message ?? 'Ready'}
        />
      </div>

      <div className="trademarkList">
        {records.length > 0 ? (
          records.map((record) => <TrademarkCard key={record.id} record={record} />)
        ) : (
          <div className="trademarkEmpty">
            {trademarksQuery.isFetching ? 'Loading records' : 'No trademark records'}
          </div>
        )}
      </div>
    </section>
  )
}

function Metric({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  )
}

function TrademarkCard({ record }: { record: TrademarkRecord }) {
  return (
    <article className="trademarkCard">
      <div className="trademarkCardHeader">
        <div>
          <span className="compareEyebrow">{record.serial_number}</span>
          <h2>{record.mark_literal || 'Untitled mark'}</h2>
        </div>
        <span className="statusPill">{record.status_label || record.status_code}</span>
      </div>

      <dl className="trademarkDetails">
        <Detail label="Owner" value={record.owner_name} />
        <Detail label="Classes" value={formatClasses(record.international_classes)} />
        <Detail label="Filing" value={record.filing_date} />
        <Detail label="Registration" value={record.registration_number} />
        <Detail label="Source updated" value={formatDateTime(record.source_updated_at)} />
        <Detail label="Last synced" value={formatDateTime(record.last_synced_at)} />
      </dl>

      <p className="trademarkGoods">{record.goods_services || 'No goods/services text'}</p>

      {record.source_record_url ? (
        <a className="recordLink" href={record.source_record_url} target="_blank" rel="noreferrer">
          <ExternalLink size={16} aria-hidden="true" />
          <span>USPTO record</span>
        </a>
      ) : null}
    </article>
  )
}

function Detail({ label, value }: { label: string; value: string | null }) {
  return (
    <div>
      <dt>{label}</dt>
      <dd>{value || '-'}</dd>
    </div>
  )
}

function formatClasses(values: number[]): string {
  return values.length > 0 ? values.join(', ') : '-'
}

function formatDateTime(value: string | null): string | null {
  if (!value) {
    return null
  }
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) {
    return value
  }
  return date.toLocaleString()
}
